import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Card, Table, message } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { toolCheckoutsRestCrudThunksForSupplier } from '../../thunks/tool_checkouts_thunks';
import {
	attachWorkOrdersToToolForSupplier,
	detachWorkOrdersFromToolForSupplier,
} from '../../thunks/tools_thunks';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getCurrency,
	getWorkOrderTimeZoneId,
} from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';
import DateTimeHover from '../date_time_component/DateTime';

const WO_TOOLS_TARGET_COLLECTION = 'woToolsIndex';

const WorkOrderToolsSection: FC<any> = ({
	workOrder,
	toolCheckouts,
	fetchCheckouts,
	attachWorkOrders,
	detachWorkOrders,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const [actionLoadingId, setActionLoadingId] = useState(null);

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);
	const currencyId = nullSafeGet('id', currency) || 'USD';

	const workOrderId = nullSafeGet('id', workOrder);
	const workOrderTimeZoneId = getWorkOrderTimeZoneId(workOrder);

	const fetchMyCheckouts = useCallback(() => {
		fetchCheckouts(
			{ pickedUpForMe: true, status: 'open' },
			WO_TOOLS_TARGET_COLLECTION,
			{ current: 1, pageSize: 50 },
			null,
			null
		);
	}, [fetchCheckouts]);

	useMountEffect(() => {
		fetchMyCheckouts();
	});

	const checkoutRecordIds = nullSafeGetOrElse(
		WO_TOOLS_TARGET_COLLECTION + '.recordsIds',
		toolCheckouts,
		[]
	);
	const checkoutRecords = checkoutRecordIds
		.map((id) => nullSafeGet('records.' + id, toolCheckouts))
		.filter(Boolean);

	const isToolLinkedToWO = useCallback(
		(checkout) => {
			const workOrders = nullSafeGetOrElse('workOrders', checkout, []);
			return workOrders.some((wo) => '' + wo.id === '' + workOrderId);
		},
		[workOrderId]
	);

	const linkedCheckouts = useMemo(
		() => checkoutRecords.filter(isToolLinkedToWO),
		[checkoutRecords, isToolLinkedToWO]
	);

	const totalCost = useMemo(() => {
		let sum = 0;
		linkedCheckouts.forEach((checkout) => {
			const cost = parseFloat(nullSafeGetOrElse('tool.costPerUse', checkout, '0'));
			if (!isNaN(cost)) {
				sum += cost;
			}
		});
		return sum;
	}, [linkedCheckouts]);

	const handleAddToWO = useCallback(
		(checkout) => {
			const toolId = nullSafeGet('tool.id', checkout);
			if (!toolId || !workOrderId) return;
			setActionLoadingId(checkout.id);
			attachWorkOrders(toolId, [workOrderId])
				.then(() => {
					fetchMyCheckouts();
				})
				.catch((err) => message.error(err))
				.finally(() => setActionLoadingId(null));
		},
		[workOrderId, attachWorkOrders, fetchMyCheckouts]
	);

	const handleRemoveFromWO = useCallback(
		(checkout) => {
			const toolId = nullSafeGet('tool.id', checkout);
			if (!toolId || !workOrderId) return;
			setActionLoadingId(checkout.id);
			detachWorkOrders(toolId, [workOrderId])
				.then(() => {
					fetchMyCheckouts();
				})
				.catch((err) => message.error(err))
				.finally(() => setActionLoadingId(null));
		},
		[workOrderId, detachWorkOrders, fetchMyCheckouts]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Tool Name',
				dataIndex: 'tool',
				render: (text, record) => (
					<span className="bodymedium">{nullSafeGetOrElse('tool.name', record, '--')}</span>
				),
			},
			{
				title: 'Location',
				dataIndex: 'stockLocation',
				render: (text, record) => (
					<span className="bodyregular">
						{nullSafeGetOrElse('tool.stockLocation.name', record, '--')}
					</span>
				),
			},
			{
				title: 'Pickup Time',
				dataIndex: 'pickedUpAt',
				render: (text, record) => {
					const pickedUpAt = nullSafeGet('pickedUpAt', record);
					return (
						<span className="bodyregular">
							{pickedUpAt ? (
								<DateTimeHover
									timestamp={pickedUpAt}
									dateFormat={'D MMM, h:mma z'}
									showDate
									timeZone={workOrderTimeZoneId}
								/>
							) : (
								'--'
							)}
						</span>
					);
				},
			},
			{
				title: 'Cost',
				dataIndex: 'costPerUse',
				render: (text, record) => {
					const cost = nullSafeGet('tool.costPerUse', record);
					return (
						<span className="bodyregular">{cost ? cost + ' ' + currencyId + '/use' : '--'}</span>
					);
				},
			},
			{
				title: 'Status',
				dataIndex: 'status',
				render: (text, record) => {
					const linked = isToolLinkedToWO(record);
					const loading = actionLoadingId === record.id;
					if (linked) {
						return (
							<div className="flex items-center">
								<CheckCircleOutlined style={{ color: '#389e0d', marginRight: 8 }} />
								<span className="bodyregular" style={{ color: '#389e0d', marginRight: 12 }}>
									Added to WO
								</span>
								<Button size="small" loading={loading} onClick={() => handleRemoveFromWO(record)}>
									Remove
								</Button>
							</div>
						);
					}
					return (
						<Button
							type="primary"
							size="small"
							loading={loading}
							onClick={() => handleAddToWO(record)}
						>
							+ Add to WO
						</Button>
					);
				},
			},
		],
		[
			isToolLinkedToWO,
			actionLoadingId,
			handleAddToWO,
			handleRemoveFromWO,
			currencyId,
			workOrderTimeZoneId,
		]
	);

	const cardExtra = useMemo(() => {
		if (linkedCheckouts.length === 0) return null;
		return (
			<span className="bodyregular text-character-secondary">
				{linkedCheckouts.length} Tool{linkedCheckouts.length !== 1 ? 's' : ''} Added:{' '}
				{totalCost.toFixed(2)} {currencyId}/use
			</span>
		);
	}, [linkedCheckouts, totalCost, currencyId]);

	return (
		<Card title="Tools" extra={cardExtra}>
			<Table
				dataSource={checkoutRecords}
				columns={columns}
				rowKey={(record) => record.id}
				loading={toolCheckouts.fetching}
				pagination={false}
				showHeader={checkoutRecords.length > 0}
				locale={{ emptyText: 'No tools currently checked out' }}
			/>
		</Card>
	);
};

const mapStateToProps = (state) => ({
	toolCheckouts: state.tool_checkouts,
});

const mapDispatchToProps = (dispatch) => ({
	fetchCheckouts: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			toolCheckoutsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		),
	attachWorkOrders: (toolId, workOrderIds) =>
		dispatch(attachWorkOrdersToToolForSupplier(toolId, workOrderIds)),
	detachWorkOrders: (toolId, workOrderIds) =>
		dispatch(detachWorkOrdersFromToolForSupplier(toolId, workOrderIds)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderToolsSection)
);
